import axios from 'axios'
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types'
import { showVersionMismatchDialog } from '@/utils/versionDialog'

// 版本不匹配错误码
const CODE_VERSION_MISMATCH = 4001

// 创建 axios 实例
const request: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  // 创作页与 Debug Playground 共用本实例。生成过程是异步轮询的，不受此限制；
  // 这里约束的是同步请求（创建任务时上传涂色卡到 S3、提示词润色等），慢链路下 30s 不够。
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 添加版本号 header
    if (config.headers) {
      config.headers['X-App-Version'] = __APP_VERSION__
    }
    // 添加认证 token
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 导出类接口（如反馈 CSV）返回的是 Blob，没有 code 字段。
    // 不先透传的话会被下面的 data.code !== 0 当成业务错误 reject。
    if (response.data instanceof Blob) {
      return response
    }

    const { data } = response
    if (data.code !== 0) {
      // 检查是否为版本不匹配错误
      if (data.code === CODE_VERSION_MISMATCH) {
        showVersionMismatchDialog()
      }
      // 业务错误
      return Promise.reject(new Error(data.message || '请求失败'))
    }
    return response
  },
  (error) => {
    // 检查是否为版本不匹配错误（HTTP 426）
    if (error.response?.status === 426 || error.response?.data?.code === CODE_VERSION_MISMATCH) {
      showVersionMismatchDialog()
      return Promise.reject(error)
    }
    if (error.response?.status === 401) {
      // 未授权，清除 token 并跳转登录
      localStorage.removeItem('token')
      window.location.href = '/login'
    }

    // 把后端的业务错误消息透出来。
    //
    // pkg/response 的 Error() 对 400/401/403/404/500 返回的是**真实 HTTP 状态码**，
    // 于是 axios 在这里就 reject 了，上面成功拦截器里那段 `data.code !== 0` 根本不会执行。
    // 不做这一步的话，调用方拿到的 e.message 是 "Request failed with status code 400"，
    // 后端精心写的中文提示（例如"该筛选条件下没有反馈"）永远到不了用户眼前。
    //
    // responseType:'blob' 的导出接口即使失败，data 也是 Blob，没有 message 字段——
    // 此处显式跳过，保持原样 reject。
    const data = error.response?.data
    if (data && !(data instanceof Blob) && typeof data.message === 'string' && data.message) {
      return Promise.reject(new Error(data.message))
    }
    return Promise.reject(error)
  }
)

export default request
